import React, { useCallback, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { NotificationsContext, NotificationsState } from '@/components/common';
import { useDashboardQuery } from '@/hooks/useDashboardQuery';
import type { AppNotification } from '@/types/notifications';
import { NOTIFICATION_SETTINGS_PATH, NOTIFICATIONS_PATH } from '@/utils/notifications';

const EMPTY: AppNotification[] = [];

/**
 * Notifications for the dashboard's bell and page. There's no notifications API yet, so real
 * sessions get none (the bell shows "You're all caught up") and read state is kept on this device
 * only; the local demo session gets the designed samples.
 */
const NotificationsProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { data, loading, error, reload } = useDashboardQuery(
    async (demo) => {
      if (import.meta.env.DEV && demo) return (await import('@/pages/dev/demoData')).demoNotifications(new URLSearchParams(window.location.search).get('notifications'));
      return EMPTY;
    },
    EMPTY,
    'We couldn’t load your notifications.',
  );
  const [readIds, setReadIds] = useState<ReadonlySet<string>>(new Set());

  const markRead = useCallback((id: string) => setReadIds((prev) => new Set(prev).add(id)), []);

  const markAllRead = useCallback(() => {
    const before = readIds;
    setReadIds(new Set(data.map((n) => n.id)));
    toast(
      (t) => (
        <span className="tw-flex tw-items-center tw-gap-4">
          All notifications marked as read
          <button
            type="button"
            // The toaster sits outside the themed shell, so no design-token colours here.
            className="tw-font-semibold tw-underline"
            onClick={() => {
              setReadIds(before);
              toast.dismiss(t.id);
            }}
          >
            Undo
          </button>
        </span>
      ),
      { id: 'notifications-read' },
    );
  }, [data, readIds]);

  const value = useMemo<NotificationsState>(
    () => ({
      items: data.map((n) => (readIds.has(n.id) ? { ...n, read: true } : n)),
      loading,
      error,
      reload,
      markRead,
      markAllRead,
      pagePath: NOTIFICATIONS_PATH,
      settingsPath: NOTIFICATION_SETTINGS_PATH,
    }),
    [data, readIds, loading, error, reload, markRead, markAllRead],
  );

  return <NotificationsContext.Provider value={value}>{children}</NotificationsContext.Provider>;
};

export default NotificationsProvider;
